import {
  AssistantCreativeDraft,
  ThreadArtifactDto,
  ThreadArtifactKind,
  WorkflowRun,
  WorkflowStepKind,
} from '@digital-twins/shared-types'

import { threadToolRendering } from '../threadToolRenderings'

/**
 * Rail / expand item for a living thread artifact (or a run-synthesized
 * StudyDoc used only by `ThreadRunResults`).
 *
 * Living items use `key === publicId`. Legacy `?artifact=stepId:slug:pos`
 * still resolves via `findThreadArtifactBySearchKey`.
 */

export interface ThreadArtifactItem {
  /** Stable key for list rendering and `?artifact=` (living: `publicId`). */
  key: string
  /** Thread-store public id when the item came from `ThreadArtifactDto`. */
  publicId?: string
  /** Closed living kind; omitted on legacy hand-built run items. */
  kind?: ThreadArtifactKind
  title: string
  body: string
  slug: string
  stepKind?: WorkflowStepKind
  /**
   * Fan-out position among creatives in a set (0 = A). Set only when more
   * than one creative is on the rail, or when a workflow step declared
   * `count` > 1. Not the global artifact list index.
   */
  index?: number
  /**
   * 0-based index within the producing step (`ThreadArtifactDto.position`).
   * Copied through so a step group can letter A from position 0.
   */
  position?: number
  /**
   * Lettered set member (`A`, `B`, …). Display-only; stored title may already
   * carry `Creative A` or `Creative A:` from promotion or Confirm.
   */
  letter?: string
  stepId: string
  stepIndex: number
  artifactIndex: number
  /**
   * Token of the producing run when lineage still exists (`sourceRunToken`).
   */
  runToken?: string
  questionIds?: Array<string>
  mimeType?: string
  /** Stimulus preview URL (enriched from thread assets). */
  mediaSrc?: string
  /** Asset registry token when this stimulus maps to a thread asset. */
  assetToken?: string
  /** Living tip optimistic version when the list DTO includes it. */
  version?: number
  /** Promote-key step id for legacy `?artifact=stepId:slug:pos` resolution. */
  sourceStepId?: string
  /** Last write on the living row or its latest tip. ISO-8601. */
  updatedAt?: string
  /**
   * Public experiment token for a study report artifact.
   * Opens the thread study pane on that study's results.
   */
  sourceStudyToken?: string
}

export const STUDY_DOC_SLUG = 'study_doc'
export const FINDINGS_SLUG = 'findings'

/**
 * Count creative draft entries still waiting on Confirm.
 *
 * Confirmed entries carry `confirmedAt` and/or `publicId`. Wave 3 wires the
 * sum across open draft sets into `ThreadArtifactPane`.
 */
export function countPendingCreativeDrafts(
  drafts: Array<AssistantCreativeDraft> | null | undefined,
): number {
  if (!drafts || drafts.length === 0) {
    return 0
  }
  let pending = 0
  for (const draft of drafts) {
    if (!draft.confirmedAt && !draft.publicId) {
      pending += 1
    }
  }
  return pending
}

/** `asset:<token>` charset from the creatives-as-artifact plan. */
const ASSET_REF_RE = /asset:([A-Za-z0-9_-]{1,64})(?![A-Za-z0-9_-])/g

/**
 * Parse `asset:<token>` refs from a markdown body.
 */
export function parseAssetRefTokens(body: string): Array<string> {
  if (!body) {
    return []
  }
  const seen = new Set<string>()
  const tokens: Array<string> = []
  ASSET_REF_RE.lastIndex = 0
  let match: RegExpExecArray | null = ASSET_REF_RE.exec(body)
  while (match) {
    const token = match[1]
    if (token && !seen.has(token)) {
      seen.add(token)
      tokens.push(token)
    }
    match = ASSET_REF_RE.exec(body)
  }
  return tokens
}

/**
 * Fold-token set from living creative DTO bodies only.
 * Never read enrichStimulusMedia output — guessed tokens must not fold.
 */
export function foldAssetTokensFromCreativeDtos(
  artifacts: Array<ThreadArtifactDto> | null | undefined,
): Set<string> {
  const foldTokens = new Set<string>()
  if (!artifacts || artifacts.length === 0) {
    return foldTokens
  }
  for (const artifact of artifacts) {
    if (artifact.kind !== 'creative') {
      continue
    }
    for (const token of parseAssetRefTokens(artifact.body ?? '')) {
      foldTokens.add(token)
    }
  }
  return foldTokens
}

/**
 * Suppress stimulus rail items whose *DTO* `assetToken` is in `foldTokens`.
 * Apply AFTER `enrichStimulusMedia` so the leftover shift pool is unchanged.
 * Lookup uses living DTO tokens, not enriched/guessed `item.assetToken`.
 */
export function filterFoldedStimulusItems(
  items: Array<ThreadArtifactItem>,
  foldTokens: ReadonlySet<string>,
  livingDtos: Array<ThreadArtifactDto> | null | undefined,
): Array<ThreadArtifactItem> {
  if (foldTokens.size === 0 || !livingDtos || livingDtos.length === 0) {
    return items
  }

  const dtoAssetTokenByPublicId = new Map<string, string>()
  for (const dto of livingDtos) {
    if (dto.kind === 'stimulus' && dto.assetToken) {
      dtoAssetTokenByPublicId.set(dto.publicId, dto.assetToken)
    }
  }
  if (dtoAssetTokenByPublicId.size === 0) {
    return items
  }

  return items.filter((item) => {
    if (item.kind !== 'stimulus') {
      return true
    }
    const publicId = item.publicId ?? item.key
    const dtoToken = dtoAssetTokenByPublicId.get(publicId)
    if (!dtoToken) {
      return true
    }
    return !foldTokens.has(dtoToken)
  })
}

/**
 * Tools whose `tool_end` should refresh the living artifact rail: the registry
 * entries whose renderer is `artifact_pane` (`THREAD_TOOL_RENDERINGS`).
 */
export function isLivingArtifactMutationTool(tool: string): boolean {
  return threadToolRendering(tool)?.refreshesArtifactRail === true
}

function stepKindForLivingKind(
  kind: ThreadArtifactKind,
): WorkflowStepKind | undefined {
  if (kind === 'brief' || kind === 'creative') {
    return 'prompt'
  }
  if (
    kind === 'report' ||
    kind === 'findings' ||
    kind === 'study_doc' ||
    kind === 'digest'
  ) {
    return 'report'
  }
  return undefined
}

/**
 * Map living DTOs from `GET /knowledge/threads/:token/artifacts` into
 * user-facing rail / inline / deep-link items. Does not read a workflow run.
 *
 * Findings stay in the living store for the assistant and SWITCH; they are
 * fact-bundle dumps, not a reader surface. StudyDoc is the twin-test write-up.
 */
export function collectThreadArtifacts(
  artifacts: Array<ThreadArtifactDto> | null | undefined,
): Array<ThreadArtifactItem> {
  if (!artifacts || artifacts.length === 0) {
    return []
  }

  const items = artifacts
    .filter((artifact) => artifact.kind !== 'findings')
    .map((artifact, artifactIndex) => {
    const kind = artifact.kind
    const item: ThreadArtifactItem = {
      key: artifact.publicId,
      publicId: artifact.publicId,
      kind,
      title: artifact.title,
      body: artifact.body ?? '',
      slug: artifact.slug,
      stepKind: stepKindForLivingKind(kind),
      stepId: '',
      stepIndex: 0,
      artifactIndex,
      runToken: artifact.sourceRunToken,
    }
    if (artifact.questionIds && artifact.questionIds.length > 0) {
      item.questionIds = artifact.questionIds
    }
    if (artifact.mimeType) {
      item.mimeType = artifact.mimeType
    }
    if (artifact.assetToken) {
      item.assetToken = artifact.assetToken
    }
    if (artifact.version != null) {
      item.version = artifact.version
    }
    if (artifact.sourceStepId) {
      item.sourceStepId = artifact.sourceStepId
    }
    if (artifact.position != null) {
      item.position = artifact.position
    }
    if (artifact.updatedAt) {
      item.updatedAt = artifact.updatedAt
    }
    if (artifact.sourceStudyToken) {
      item.sourceStudyToken = artifact.sourceStudyToken
    }
    return item
  })

  return letterLivingCreatives(items)
}

/** `Creative A` or `Creative A: subject` — subject is optional after promotion. */
const STORED_CREATIVE_LETTER = /^Creative\s+([A-Z]+)(?:\s*:|$)/i

function letterFromStoredTitle(title: string): string | null {
  const match = title.match(STORED_CREATIVE_LETTER)
  if (!match) {
    return null
  }
  return match[1].toUpperCase()
}

function positionalCreativeLetter(index: number): string {
  if (index < 0) {
    return 'A'
  }
  return String.fromCharCode(65 + (index % 26))
}

/**
 * When two or more living creatives share a set, stamp A/B/C so the list
 * does not collapse to the same "Creatives: {preview}" row. A lone creative
 * stays the unlettered Creatives category.
 *
 * Confirm writes A then B then C, and `listLivingByThread` is newest-first,
 * so the wire order is C,B,A. Reorder lettered titles A→Z in the creative
 * slots only — other kinds keep their newest-first place. Group by producing
 * step so two fan-out sets on one thread do not share one A–Z sequence.
 */
function letterLivingCreatives(
  items: Array<ThreadArtifactItem>,
): Array<ThreadArtifactItem> {
  const slotsBySet = new Map<string, Array<number>>()
  for (let index = 0; index < items.length; index += 1) {
    if (items[index]?.kind !== 'creative') {
      continue
    }
    const setKey = items[index]?.sourceStepId ?? ''
    const slots = slotsBySet.get(setKey) ?? []
    slots.push(index)
    slotsBySet.set(setKey, slots)
  }

  for (const slots of slotsBySet.values()) {
    letterCreativeSlots(items, slots)
  }
  return items
}

function letterCreativeSlots(
  items: Array<ThreadArtifactItem>,
  slots: Array<number>,
): void {
  if (slots.length <= 1) {
    return
  }

  const creatives = slots.map((slot) => items[slot])
  const allLettered = creatives.every((item) => {
    return Boolean(item && letterFromStoredTitle(item.title))
  })
  const ordered = allLettered
    ? [...creatives].sort((left, right) => {
        const letterLeft = letterFromStoredTitle(left?.title ?? '') ?? ''
        const letterRight = letterFromStoredTitle(right?.title ?? '') ?? ''
        return letterLeft.localeCompare(letterRight)
      })
    : creatives

  slots.forEach((slot, index) => {
    const item = ordered[index]
    if (!item) {
      return
    }
    item.index = index
    item.letter =
      letterFromStoredTitle(item.title) ?? positionalCreativeLetter(index)
    items[slot] = item
  })
}

/**
 * Attach image/video URLs from the thread asset registry onto stimulus items.
 * Match order: `assetToken` → title/label → mimeType → leftover unpaired.
 */
export function enrichStimulusMedia(
  items: Array<ThreadArtifactItem>,
  assets: Array<{
    token: string
    ref: {
      publicUrl?: string | null
      mimeType?: string
      label?: string
      kind?: string
    }
  }>,
): Array<ThreadArtifactItem> {
  const previewable = assets.filter((asset) => {
    const kind = asset.ref.kind
    return (
      Boolean(asset.ref.publicUrl) && (kind === 'image' || kind === 'video')
    )
  })
  if (previewable.length === 0) {
    return items
  }

  const unused = [...previewable]

  return items.map((item) => {
    if (item.kind !== 'stimulus') {
      return item
    }

    let asset: (typeof unused)[number] | undefined

    if (item.assetToken) {
      const byToken = unused.findIndex(
        (candidate) => candidate.token === item.assetToken,
      )
      if (byToken >= 0) {
        asset = unused.splice(byToken, 1)[0]
      }
    }

    if (!asset) {
      const byLabel = unused.findIndex((candidate) => {
        const label = (candidate.ref.label ?? '').trim()
        return label.length > 0 && label === item.title.trim()
      })
      if (byLabel >= 0) {
        asset = unused.splice(byLabel, 1)[0]
      }
    }

    if (!asset && item.mimeType) {
      const byMime = unused.findIndex(
        (candidate) => candidate.ref.mimeType === item.mimeType,
      )
      if (byMime >= 0) {
        asset = unused.splice(byMime, 1)[0]
      }
    }
    if (!asset && unused.length > 0) {
      asset = unused.shift()
    }
    if (!asset?.ref.publicUrl) {
      return item
    }
    return {
      ...item,
      mediaSrc: asset.ref.publicUrl,
      mimeType: item.mimeType ?? asset.ref.mimeType,
      assetToken: item.assetToken ?? asset.token,
    }
  })
}

function studyDocMarkdownFromFinding(finding: {
  result?: unknown
}): string {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return ''
  }
  const raw = (payload as Record<string, unknown>).study_doc
  return typeof raw === 'string' ? raw.trim() : ''
}

function factBundleMarkdownFromFinding(finding: {
  result?: unknown
}): string {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return ''
  }
  const factBundle = (payload as Record<string, unknown>).fact_bundle
  if (
    !factBundle ||
    typeof factBundle !== 'object' ||
    Array.isArray(factBundle)
  ) {
    return ''
  }

  const bundle = factBundle as Record<string, unknown>
  const stake = typeof bundle.stake === 'string' ? bundle.stake.trim() : ''
  const questionId =
    typeof bundle.question_id === 'string' && bundle.question_id.trim()
      ? bundle.question_id.trim()
      : 'q1'
  const facts = Array.isArray(bundle.facts) ? bundle.facts : []
  const lines = [`# Findings (${questionId})`, '']

  if (stake) {
    lines.push(stake, '')
  }
  lines.push('## Facts', '')

  for (const fact of facts) {
    if (!fact || typeof fact !== 'object' || Array.isArray(fact)) {
      continue
    }
    const opId =
      typeof (fact as Record<string, unknown>).op_id === 'string'
        ? (fact as Record<string, unknown>).op_id
        : 'op'
    lines.push(
      `### \`${opId}\``,
      '',
      '```json',
      JSON.stringify(fact, null, 2),
      '```',
      '',
    )
  }

  const allowedNumbers = Array.isArray(bundle.allowed_numbers)
    ? bundle.allowed_numbers
    : []
  if (allowedNumbers.length > 0) {
    lines.push('## Allowed numbers', '', allowedNumbers.join(', '), '')
  }

  return `${lines.join('\n').trimEnd()}\n`
}

/**
 * THE POET write-up lives on `quick_study` findings for the run results
 * surface. Not the living rail source of truth.
 */
export function studyDocArtifactFromRun(
  run: WorkflowRun | null | undefined,
): ThreadArtifactItem | null {
  if (!run) {
    return null
  }

  const bodies: Array<string> = []
  let first: {
    stepId: string
    stepIndex: number
    title: string
  } | null = null

  for (const step of run.steps) {
    const output = step.outputOverride ?? step.output
    if (!output || output.kind !== 'quick_study') {
      continue
    }
    for (const finding of output.findings) {
      const markdown = studyDocMarkdownFromFinding(finding)
      if (!markdown) {
        continue
      }
      if (!first) {
        first = {
          stepId: step.stepId,
          stepIndex: step.stepIndex,
          title: step.title,
        }
      }
      bodies.push(markdown)
    }
  }

  if (!first || bodies.length === 0) {
    return null
  }

  return {
    key: `${first.stepId}:${STUDY_DOC_SLUG}:0`,
    kind: 'study_doc',
    title: first.title,
    body: bodies.join('\n\n'),
    slug: STUDY_DOC_SLUG,
    stepKind: 'report',
    stepId: first.stepId,
    stepIndex: first.stepIndex,
    artifactIndex: 0,
    runToken: run.token,
  }
}

